import { MutableRefObject, ReactElement } from 'react';
import classnames from 'classnames';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import StarIcon from '../../assets/icons/StarIcon';
import useMobileApp from '../../hooks/mobile-app/useMobileApp';
import useOfferImpression from '../../hooks/analytics/useOfferImpression';
import { DealEventProps } from '../../utils/analytics/dealEventProps';
import DiscountCardTag from '../../features/discount-exclusivity/DiscountCardTag';
import type { DiscountCardTag as DiscountCardTagType } from '../../features/discount-exclusivity/useDiscountCardTag';

export type Variant = 1 | 2 | 3 | 4 | 5 | 6;

interface Props {
  title: string;
  description: string;
  footer?: string;
  className?: string;
  onClick?: () => void;
  variant: Variant;
  image?: string;
  isPlaceholder?: boolean;
  badge?: string;
  tag?: DiscountCardTagType;
  /** When set on an applied card, renders an inline "Remove" link (prototype). */
  onRemove?: () => void;
  analytics?: DealEventProps;
}

const variantBg: Record<NonNullable<Props['variant']>, string> = {
  1: 'bg-gradient-to-tr from-orange-400 to-red-500',
  2: 'bg-gradient-to-r from-pink-500 to-purple-600 dark:from-purple-800 dark:to-slate-700',
  3: 'bg-gradient-to-r from-cyan-400 to-teal-500 dark:from-emerald-700 dark:to-teal-800',
  4: 'bg-gradient-to-r from-blue-500 to-indigo-600 dark:from-indigo-800 dark:to-purple-700',
  5: 'bg-gradient-to-r from-orange-500 to-red-500 dark:from-amber-700 dark:to-red-800',
  6: 'bg-gradient-to-r from-green-500 to-lime-500 dark:from-emerald-800 dark:to-green-700',
};

const bubbles: Record<NonNullable<Props['variant']>, { b1: string; b2: string }> = {
  1: {
    b1: 'absolute -right-5 -top-5 w-20 h-20 bg-white/20 rounded-full',
    b2: 'absolute -right-8 -bottom-4 w-16 h-16 bg-white/10 rounded-full',
  },
  2: {
    b1: 'absolute -right-2 -top-2 w-16 h-16 bg-white/20 rounded-full',
    b2: 'absolute -right-8 -bottom-4 w-20 h-20 bg-white/10 rounded-full',
  },
  3: {
    b1: 'absolute -right-4 -top-4 w-20 h-20 bg-white/20 rounded-full',
    b2: 'absolute -right-12 -bottom-2 w-16 h-16 bg-white/10 rounded-full',
  },
  4: {
    b1: 'absolute -right-6 -top-6 w-24 h-24 bg-white/20 rounded-full',
    b2: 'absolute -right-2 -bottom-8 w-18 h-18 bg-white/10 rounded-full',
  },
  5: {
    b1: 'absolute -right-3 -top-3 w-18 h-18 bg-white/20 rounded-full',
    b2: 'absolute -right-10 -bottom-6 w-22 h-22 bg-white/10 rounded-full',
  },
  6: {
    b1: 'absolute -right-5 -top-5 w-20 h-20 bg-white/20 rounded-full',
    b2: 'absolute -right-8 -bottom-4 w-16 h-16 bg-white/10 rounded-full',
  },
} as const;

const badgeColors: Record<string, string> = {
  'FREE ITEM': 'bg-green-500',
  REWARDS: 'bg-amber-500',
  PROMO: 'bg-blue-500',
  DEAL: 'bg-red-500',
};

export default function DiscountCard(props: Props): ReactElement {
  const { title, className, onClick, image, isPlaceholder, badge, tag, analytics } = props;
  const { t } = useTranslation(null, { keyPrefix: 'Components:Carousel:DealsCarousel' });
  const impressionRef = useOfferImpression(analytics);

  const badgeLabel = badge ? t(`badges.${badge}`, { defaultValue: badge }) : undefined;

  // The badge is only meaningful when we are NOT showing the offer's own image:
  // either we fell back to a placeholder image, or there is no image at all.
  if (image) {
    return <ImageCard {...props} badgeLabel={badgeLabel} showBadge={!!isPlaceholder} impressionRef={impressionRef} />;
  }

  return (
    <div
      ref={impressionRef}
      className={classnames(
        'h-[150px] rounded-2xl p-4 flex items-center justify-between',
        'relative overflow-hidden shadow-lg cursor-pointer',
        variantBg[props.variant],
        className,
      )}
      onClick={onClick}
    >
      <div className="z-10 h-full space-y-2 flex flex-col justify-between">
        <div className="flex flex-col gap-1">
          {badgeLabel && (
            <div className="w-fit rounded-full bg-white/25 px-2 py-0.5 text-[10px] font-bold uppercase text-white">
              {badgeLabel}
            </div>
          )}
          <div className="text-white font-semibold text-md leading-tight line-clamp-2">{title}</div>
          {tag && <DiscountCardTag tag={tag} onColoredBg />}
        </div>
        <Footer {...props} />
      </div>

      <div className="z-10">
        <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
          <StarIcon className="w-6 h-6 text-white" />
        </div>
      </div>

      <div className={bubbles[props.variant].b1} />
      <div className={bubbles[props.variant].b2} />
    </div>
  );
}

interface ImageCardProps extends Props {
  showBadge?: boolean;
  badgeLabel?: string;
  impressionRef?: MutableRefObject<any>;
}

function ImageCard({
  title,
  description,
  className,
  onClick,
  image,
  badge,
  tag,
  onRemove,
  showBadge,
  badgeLabel,
  impressionRef,
}: ImageCardProps): ReactElement {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Carousel:DealsCarousel' });
  const badgeColor = badgeColors[badge] ?? badgeColors['DEAL'];
  const displayDescription = description || t('view_more');
  const isBlocked = tag?.status === 'replaces';
  const isApplied = tag?.status === 'applied';

  return (
    <div
      ref={impressionRef}
      className={classnames(
        // `h-full` so every card fills its carousel slide — the slides already stretch to the
        // tallest card (flex items-stretch), so this makes all cards equal height, no fixed value.
        'group flex flex-col h-full rounded-2xl overflow-hidden shadow-section cursor-pointer border transition-colors duration-200',
        isBlocked ? 'border-amber-500/60' : 'border-brand-border hover:border-brand-primary',
        className,
      )}
      onClick={onClick}
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={image}
          alt=""
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        {showBadge && badgeLabel && (
          <div
            className={classnames(
              'absolute top-3 left-3 rounded-full px-2 py-1 text-xs font-bold uppercase text-white',
              badgeColor,
            )}
          >
            {badgeLabel}
          </div>
        )}
      </div>

      <div className="bg-white px-3 py-2.5 min-h-[72px] flex flex-col flex-1 gap-1 overflow-hidden">
        <div className="font-semibold text-[15px] leading-tight text-brand-text-primary line-clamp-2">{title}</div>
        {tag && <DiscountCardTag tag={tag} />}
        {isBlocked ? (
          <DiscountCardReplacesDetail
            activeName={tag?.activeName}
            activeCount={tag?.activeKind === 'offer' ? tag?.activeCount : undefined}
            activeTotal={tag?.activeTotal}
          />
        ) : isApplied && onRemove ? (
          <button
            type="button"
            data-test="discount-card-remove"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="w-fit text-xs font-medium text-brand-primary underline-offset-4 hover:underline"
          >
            {t('deals.buttons.remove')}
          </button>
        ) : (
          // Discount-exclusivity cards (those with a status `tag`) show only title + status label,
          // per the prototype — the raw discount description is dropped. Other deal cards keep it.
          <div className="flex items-center justify-between gap-2">
            {!tag && (
              <div className="text-muted-foreground text-xs leading-none line-clamp-1 flex-1">{displayDescription}</div>
            )}
            {onClick && <ArrowRight className="ml-auto shrink-0 w-4 h-4 text-muted-foreground" />}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The blocked-by-conflict follow-up shown under the card's status row (PM / Lovable prototype):
 * just a "Replaces …" line naming the active discount. The general one-per-order note was removed
 * from the card per CTO (2026-08-03) — the "Cannot be combined with …" status row already says it.
 */
function DiscountCardReplacesDetail({
  activeName,
  activeCount,
  activeTotal,
}: {
  activeName?: string;
  activeCount?: number;
  activeTotal?: string;
}): ReactElement | null {
  const { t } = useTranslation(null, { keyPrefix: 'Features:DiscountExclusivity' });

  // Several active offers → count + total; otherwise the single "Replaces {name}" line (item 6).
  if ((activeCount ?? 0) > 1) {
    return (
      <p className="text-muted-foreground text-[11px] leading-snug">
        {t('card.replaces_offers', { count: activeCount, total: activeTotal })}
      </p>
    );
  }

  if (!activeName) {
    return null;
  }

  return (
    <p className="text-muted-foreground text-[11px] leading-snug">{t('card.replaces_name', { name: activeName })}</p>
  );
}

const buttonColors: Record<NonNullable<Props['variant']>, string> = {
  1: 'bg-white text-orange-400 hover:bg-white/90 dark:text-orange-800',
  2: 'bg-white text-purple-600 hover:bg-white/90 dark:text-purple-800',
  3: 'bg-white text-teal-600 hover:bg-white/90 dark:text-teal-800',
  4: 'bg-white text-blue-600 hover:bg-white/90 dark:text-purple-800',
  5: 'bg-white text-orange-600 hover:bg-white/90 dark:text-orange-800',
  6: 'bg-white text-green-600 hover:bg-white/90 dark:text-emerald-800',
};

const Footer = ({ footer, variant }: Props) => {
  const { isMobileApp } = useMobileApp();

  if (!footer) {
    return null;
  }

  return (
    <button
      className={classnames(
        'font-normal text-sm mt-sm min-w-[116px] w-fit py-2 px-3 hover:scale-[1.02] active:scale-[0.98] transform transition-all duration-200',
        buttonColors[variant],
        { 'rounded-full': isMobileApp, 'rounded-xl': !isMobileApp },
      )}
    >
      {footer}
    </button>
  );
};
